import { TrackerRect } from './barcode-tracker-selector';
import {
  BRACKET_ARM_FRACTION,
  BRACKET_ARM_MAX_PX,
  BRACKET_CORNER_RADIUS_PX,
} from './barcode-tracker-constants';

interface Pt { x: number; y: number; }

function dist(a: Pt, b: Pt): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  return Math.hypot(dx, dy);
}

function pointAlong(from: Pt, to: Pt, distance: number): Pt {
  const len = dist(from, to);
  if (len <= 0) return { x: from.x, y: from.y };
  const t = distance / len;
  return { x: from.x + (to.x - from.x) * t, y: from.y + (to.y - from.y) * t };
}

function fmt(n: number): string {
  // 2-decimal precision keeps the `d` string short while preserving sub-pixel smoothness during morph
  return String(Math.round(n * 100) / 100);
}

/** Build the SVG `d` attribute for the four L-bracket corners of an
 *  axis-aligned rect. Each corner gets a tangent-arc elbow (rounded
 *  outward), arms capped at `min(BRACKET_ARM_MAX_PX, edge*BRACKET_ARM_FRACTION)`
 *  and arc radius capped at `min(BRACKET_CORNER_RADIUS_PX, armPrev/2,
 *  armNext/2)` so the two legs always keep a visible straight segment.
 *  Returns an empty string for a zero-dimension rect.
 *
 *  Mirrors `buildBracketPath` in
 *  client/ios-overlay/App/App/BarcodeTrackerOverlay.swift:234-257.
 */
export function buildBracketPath(rect: TrackerRect): string {
  if (rect.w <= 0 || rect.h <= 0) return '';
  // 4 corners in clockwise order (top-left → top-right → bottom-right → bottom-left)
  // — under SVG's y-down convention this is geometrically clockwise.
  const corners: Pt[] = [
    { x: rect.x,           y: rect.y           },
    { x: rect.x + rect.w,  y: rect.y           },
    { x: rect.x + rect.w,  y: rect.y + rect.h  },
    { x: rect.x,           y: rect.y + rect.h  },
  ];
  const segments: string[] = [];
  for (let i = 0; i < 4; i++) {
    const prev = corners[(i + 3) % 4];
    const curr = corners[i];
    const next = corners[(i + 1) % 4];
    const edgeToPrev = dist(curr, prev);
    const edgeToNext = dist(curr, next);
    const armPrev = Math.min(BRACKET_ARM_MAX_PX, BRACKET_ARM_FRACTION * edgeToPrev);
    const armNext = Math.min(BRACKET_ARM_MAX_PX, BRACKET_ARM_FRACTION * edgeToNext);
    const armPrevPt = pointAlong(curr, prev, armPrev);
    const armNextPt = pointAlong(curr, next, armNext);
    const radius = Math.min(BRACKET_CORNER_RADIUS_PX, armPrev * 0.5, armNext * 0.5);

    if (radius <= 0 || armPrev <= 0 || armNext <= 0) {
      // degenerate — draw two straight legs through the corner
      segments.push(`M${fmt(armPrevPt.x)} ${fmt(armPrevPt.y)}L${fmt(curr.x)} ${fmt(curr.y)}L${fmt(armNextPt.x)} ${fmt(armNextPt.y)}`);
      continue;
    }

    // Tangent points: r away from the corner along each arm (since
    // the rect's adjacent edges are perpendicular, this is exactly
    // where the inscribed quarter-circle is tangent to each arm).
    const uPrev = { x: (prev.x - curr.x) / edgeToPrev, y: (prev.y - curr.y) / edgeToPrev };
    const uNext = { x: (next.x - curr.x) / edgeToNext, y: (next.y - curr.y) / edgeToNext };
    const tangentIn  = { x: curr.x + uPrev.x * radius, y: curr.y + uPrev.y * radius };
    const tangentOut = { x: curr.x + uNext.x * radius, y: curr.y + uNext.y * radius };

    // Sweep flag: cross-product z of (uPrev × uNext). Negative → SVG
    // sweep=1 (clockwise in y-down), which curves the arc around the
    // outside of the corner — i.e. the rounded elbow of the bracket.
    const cross = uPrev.x * uNext.y - uPrev.y * uNext.x;
    const sweep = cross < 0 ? 1 : 0;

    segments.push(
      `M${fmt(armPrevPt.x)} ${fmt(armPrevPt.y)}` +
      `L${fmt(tangentIn.x)} ${fmt(tangentIn.y)}` +
      `A${fmt(radius)} ${fmt(radius)} 0 0 ${sweep} ${fmt(tangentOut.x)} ${fmt(tangentOut.y)}` +
      `L${fmt(armNextPt.x)} ${fmt(armNextPt.y)}`,
    );
  }
  return segments.join('');
}
